import { request, ApiError } from '@/api/http';
import type {
  ActivityLog,
  ActivityLogListParams,
  Delivery,
  DeliveryBulkRetryResult,
  DeliveryDetail,
  DeliveryRetryResult,
  DiscoveredLink,
  DeliveryResendResult,
  FetchRun,
  Paginated,
} from '@/api/types';
import { normalizeDelivery } from '@/api/normalizeEntity';
import { filterValidDeliveryIds, isUuid } from '@/utils/uuid';

function requireDeliveryId(id: string | null | undefined): string {
  if (id == null || typeof id !== 'string') {
    throw new ApiError('Invalid delivery ID', 400);
  }
  const trimmed = id.trim();
  if (!isUuid(trimmed)) {
    throw new ApiError('Invalid delivery ID', 400);
  }
  return trimmed;
}

function buildActivityLogQuery(params?: ActivityLogListParams): string {
  const q = new URLSearchParams();
  if (params?.page != null) q.set('page', String(params.page));
  if (params?.limit != null) q.set('limit', String(params.limit));
  if (params?.job_id) q.set('job_id', params.job_id);
  if (params?.job_item_id) q.set('job_item_id', params.job_item_id);
  if (params?.search_id) q.set('search_id', params.search_id);
  if (params?.source_id) q.set('source_id', params.source_id);
  if (params?.kind) q.set('kind', params.kind);
  if (params?.status) q.set('status', params.status);
  if (params?.level) q.set('level', params.level);
  if (params?.created_after) q.set('created_after', params.created_after);
  if (params?.created_before) q.set('created_before', params.created_before);
  return q.toString();
}

export const activityService = {
  deliveries: async (limit = 50) => {
    const rows = await request<Delivery[]>(`/deliveries?limit=${limit}`);
    return rows.map(normalizeDelivery);
  },
  deliveriesPaginated: (
    page = 1,
    limit = 20,
    opts?: {
      status?: string;
      search_id?: string;
      recipient_id?: string;
      source?: string;
      q?: string;
      sent_after?: string;
      sent_before?: string;
    }
  ) => {
    const q = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (opts?.status) q.set('status', opts.status);
    if (opts?.search_id) q.set('search_id', opts.search_id);
    if (opts?.recipient_id) q.set('recipient_id', opts.recipient_id);
    if (opts?.source) q.set('source', opts.source);
    if (opts?.q) q.set('q', opts.q);
    if (opts?.sent_after) q.set('sent_after', opts.sent_after);
    if (opts?.sent_before) q.set('sent_before', opts.sent_before);
    return request<Paginated<Delivery>>(`/deliveries/paginated?${q}`).then((page) => ({
      ...page,
      items: page.items.map(normalizeDelivery),
    }));
  },
  getDeliveryDetail: async (id: string) =>
    normalizeDelivery(await request<DeliveryDetail>(`/deliveries/${requireDeliveryId(id)}`)),
  retryDelivery: (id: string) =>
    request<DeliveryRetryResult>(`/deliveries/${requireDeliveryId(id)}/retry`, { method: 'POST' }),
  bulkRetryDeliveries: (ids: string[]) => {
    const validIds = filterValidDeliveryIds(ids);
    if (validIds.length === 0) {
      throw new ApiError('No valid delivery IDs to retry', 400);
    }
    return request<DeliveryBulkRetryResult>('/deliveries/bulk/retry', {
      method: 'POST',
      body: JSON.stringify({ ids: validIds }),
    });
  },
  resendDelivery: (id: string) =>
    request<DeliveryResendResult>(`/deliveries/${requireDeliveryId(id)}/resend`, { method: 'POST' }),
  discoveredLinks: (limit = 50) => request<DiscoveredLink[]>(`/discovered-links?limit=${limit}`),
  discoveredLinksPaginated: (page = 1, limit = 20) =>
    request<Paginated<DiscoveredLink>>(`/discovered-links/paginated?page=${page}&limit=${limit}`),
  fetchRuns: (limit = 20) => request<FetchRun[]>(`/fetch-runs?limit=${limit}`),
  bulkDeleteDeliveries: (ids: string[], opts?: { alsoDeleteRelatedJobs?: boolean }) =>
    request<{ deleted: number; jobs_deleted?: number; failed?: number; errors?: { id: string; error: string }[] }>(
      '/deliveries/bulk/delete',
      {
        method: 'POST',
        body: JSON.stringify({
          ids: ids.map(requireDeliveryId),
          also_delete_related_jobs: opts?.alsoDeleteRelatedJobs ?? false,
        }),
      },
    ),
  relatedJobForDelivery: (deliveryId: string) =>
    request<{ job_id: string; status: string } | null>(
      `/deliveries/${requireDeliveryId(deliveryId)}/related-job`
    ),
  cleanupDeliveries: (payload: {
    older_than_days?: number;
    status?: string;
    limit?: number;
    also_delete_related_jobs?: boolean;
  }) =>
    request<{ deleted: number; jobs_deleted?: number; failed?: number }>('/deliveries/cleanup', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  activityLogs: (params?: ActivityLogListParams) => {
    const q = buildActivityLogQuery(params);
    return request<Paginated<ActivityLog>>(`/activity/logs${q ? `?${q}` : ''}`);
  },
  bulkClearActivityLogs: (ids: string[], opts?: { alsoDeleteRelatedJobs?: boolean }) =>
    request<{ deleted: number; jobs_deleted?: number; failed?: number }>('/activity/logs/bulk/clear', {
      method: 'POST',
      body: JSON.stringify({
        ids,
        also_delete_related_jobs: opts?.alsoDeleteRelatedJobs ?? false,
      }),
    }),
  cleanupActivityLogs: (payload: { older_than_days?: number; kind?: string; limit?: number }) =>
    request<{ deleted: number }>('/activity/logs/cleanup', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  clearActivityForSearch: (searchId: string) =>
    request<{ deleted: number }>(`/activity/logs/clear/by-search/${searchId}`, { method: 'POST' }),
  clearActivityForSource: (sourceId: string) =>
    request<{ deleted: number }>(`/activity/logs/clear/by-source/${sourceId}`, { method: 'POST' }),
};
